import { Injectable, NgZone } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';

/**
 * Generic, client agnostic bridge between the Angular app and the inline
 * script of the hosting `index.<client>.html`.
 *
 * The Angular app never speaks a TSP specific dialect: it only emits/accepts
 * the `lm:*` messages documented below on its own window. Each embedded
 * client (MCF, and any future one) keeps its own vendor protocol inside its
 * index html and translates to/from these events.
 *
 * Inbound (host page -> app)
 *  - `lm:navigate` `{ path, requestId? }` navigate to an in-app route.
 *    The legacy `{ eventType: 'navigation', path }` message is still accepted.
 *
 * Outbound (app -> host page)
 *  - `lm:ready`              app bootstrapped and listening
 *  - `lm:navigation-start`   `{ path, url, requestId? }`
 *  - `lm:navigation-end`     `{ path, url, requestId? }` route resolved *and* painted
 *  - `lm:navigation-failed`  `{ path, url, requestId?, reason }`
 *
 * `requestId` is echoed back on the terminal event of the navigation it
 * started, so the host can wait for the real completion of a navigation it
 * requested instead of guessing from url changes.
 */
export const IFRAME_BRIDGE_EVENTS = {
  READY: 'lm:ready',
  NAVIGATE: 'lm:navigate',
  NAVIGATION_START: 'lm:navigation-start',
  NAVIGATION_END: 'lm:navigation-end',
  NAVIGATION_FAILED: 'lm:navigation-failed',
  /** Contract kept for the bridges written before `lm:navigate` existed. */
  LEGACY_NAVIGATE: 'navigation',
};

type NavigationFailureReason = 'cancelled' | 'error' | 'rejected';

@Injectable({
  providedIn: 'root',
})
export class IframeBridgeService {
  private initialized = false;
  /** requestId waiting for its navigation to be picked up by the router, keyed by normalized path. */
  private requestsByPath = new Map<string, string>();
  /** requestId of an in-flight navigation, keyed by the router navigation id. */
  private requestsByNavigationId = new Map<number, string>();

  constructor(private router: Router, private zone: NgZone) {}

  /**
   * Starts the bridge. No-op outside of an iframe or when called twice.
   */
  public init(): void {
    if (this.initialized || window.self === window.top) {
      return;
    }
    this.initialized = true;

    window.addEventListener('message', this.onHostMessage);
    this.listenToRouter();
    this.emit(IFRAME_BRIDGE_EVENTS.READY, { path: this.normalize(this.router.url), url: this.router.url });
  }

  public destroy(): void {
    if (!this.initialized) {
      return;
    }
    window.removeEventListener('message', this.onHostMessage);
    this.requestsByPath.clear();
    this.requestsByNavigationId.clear();
    this.initialized = false;
  }

  private onHostMessage = (event: MessageEvent) => {
    const data = (event && event.data) || {};

    // Public, documented contract: any parent window may post
    // `{ eventType: 'navigation', path }` directly to the iframe. Never
    // restrict this by origin or source — integrators rely on it as-is.
    if (data.eventType === IFRAME_BRIDGE_EVENTS.LEGACY_NAVIGATE) {
      this.zone.run(() => this.navigate(data.path));
      return;
    }

    // The correlated protocol is internal: only the inline script of our own
    // document speaks it, so a parent frame cannot forge a requestId.
    if (data.eventType === IFRAME_BRIDGE_EVENTS.NAVIGATE && event.source === window) {
      this.zone.run(() => this.navigate(data.path, data.requestId));
    }
  };

  private navigate(rawPath: string, requestId?: string): void {
    const path = this.normalize(rawPath);
    if (!path) {
      return;
    }

    // Already there: nothing will be routed, so acknowledge right away.
    if (this.normalize(this.router.url) === path) {
      this.emitAfterRender(IFRAME_BRIDGE_EVENTS.NAVIGATION_END, { path, url: this.router.url, requestId });
      return;
    }

    if (requestId) {
      this.requestsByPath.set(path, requestId);
    }
    this.router.navigate([path]).then(
      (navigated) => {
        // Guard fired only when the router refused the navigation before
        // emitting NavigationStart (blocking guard, unknown route).
        if (!navigated && this.requestsByPath.get(path) === requestId) {
          this.requestsByPath.delete(path);
          this.emitFailed(path, path, requestId, 'rejected');
        }
      },
      () => {
        if (this.requestsByPath.get(path) === requestId) {
          this.requestsByPath.delete(path);
        }
        this.emitFailed(path, path, requestId, 'error');
      }
    );
  }

  private listenToRouter(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const path = this.normalize(event.url);
        const requestId = this.requestsByPath.get(path);
        if (requestId) {
          this.requestsByPath.delete(path);
          this.requestsByNavigationId.set(event.id, requestId);
        }
        this.emit(IFRAME_BRIDGE_EVENTS.NAVIGATION_START, { path, url: event.url, requestId });
        return;
      }

      if (event instanceof NavigationEnd) {
        const requestId = this.takeRequestId(event.id);
        const url = event.urlAfterRedirects || event.url;
        this.emitAfterRender(IFRAME_BRIDGE_EVENTS.NAVIGATION_END, { path: this.normalize(url), url, requestId });
        return;
      }

      if (event instanceof NavigationCancel || event instanceof NavigationError) {
        const requestId = this.takeRequestId(event.id);
        const reason: NavigationFailureReason = event instanceof NavigationCancel ? 'cancelled' : 'error';
        this.emitFailed(this.normalize(event.url), event.url, requestId, reason);
      }
    });
  }

  private takeRequestId(navigationId: number): string | undefined {
    const requestId = this.requestsByNavigationId.get(navigationId);
    this.requestsByNavigationId.delete(navigationId);
    return requestId;
  }

  private emitFailed(path: string, url: string, requestId: string | undefined, reason: NavigationFailureReason): void {
    this.emit(IFRAME_BRIDGE_EVENTS.NAVIGATION_FAILED, { path, url, requestId, reason });
  }

  /**
   * NavigationEnd only means the router settled; the new view is painted one
   * frame later. Waiting for that frame is what lets the host keep its loader
   * up until the page is actually visible.
   */
  private emitAfterRender(eventType: string, payload: Record<string, any>): void {
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => this.emit(eventType, payload)));
    });
  }

  private emit(eventType: string, payload: Record<string, any> = {}): void {
    // '*' on purpose: this is a self-post, and a sandboxed frame has an opaque
    // origin that would make a strict targetOrigin throw. Matches the
    // targetOrigin the previous `lm:ready` post used.
    window.postMessage({ eventType, ...payload }, '*');
  }

  /** `/sso/trips?foo=1` and `trips` both normalize to `trips`. */
  private normalize(path: string): string {
    return String(path || '')
      .split('?')[0]
      .split('#')[0]
      .replace(/^\/+|\/+$/g, '');
  }
}
